// Package imports:
import React, { useRef, useEffect } from 'react';
import cx from 'classnames';

const CDN_BASE = 'https://cdn.livemarketdata.com/KycLogos';

interface IOwnProps {
    logos: string[],
    speed?: number,
    pauseOnHover?: boolean,
    className?: string
}

const LogoMarquee: React.FC<IOwnProps> = ({
    logos,
    speed = 60,
    pauseOnHover = true,
    className
}) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const offset = useRef(0);
    const velocity = useRef(1);
    const targetVelocity = useRef(1);
    const raf = useRef(0);

    useEffect(() => {
        const track = trackRef.current;
        if (!track || logos.length === 0) return;

        let last = performance.now();

        const tick = (now: number) => {
            const dt = (now - last) / 1000;
            last = now;

            // Decelerate slowly (~1s), accelerate quickly (~0.5s)
            const decay = targetVelocity.current < velocity.current ? 3 : 6;
            velocity.current += (targetVelocity.current - velocity.current) * (1 - Math.exp(-decay * dt));

            // Snap when close enough to avoid drift
            if (Math.abs(velocity.current - targetVelocity.current) < 0.001) {
                velocity.current = targetVelocity.current;
            }

            if (velocity.current > 0.001) {
                const half = track.scrollWidth / 2;
                offset.current -= (half / speed) * dt * velocity.current;
                if (offset.current <= -half) offset.current += half;
                track.style.transform = `translate3d(${offset.current}px, 0, 0)`;
            }

            raf.current = requestAnimationFrame(tick);
        };

        raf.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf.current);
    }, [speed, logos.length]);

    if (logos.length === 0) return null;

    const onEnter = () => { if (pauseOnHover) targetVelocity.current = 0; };
    const onLeave = () => { if (pauseOnHover) targetVelocity.current = 1; };

    const renderLogo = (name: string) => (
        <div className="logo-marquee__logo">
            <img
                className="logo-marquee__img logo-marquee__img--default"
                src={`${CDN_BASE}/${name}.svg`}
                alt={name}
            />
            <img
                className="logo-marquee__img logo-marquee__img--color"
                src={`${CDN_BASE}/${name}-color.svg`}
                alt=""
            />
        </div>
    );

    return (
        <div
            className={cx('KCL_logo-marquee', className)}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
        >
            <div className="logo-marquee__track" ref={trackRef}>
                {logos.map((name, i) => (
                    <div className="logo-marquee__item" key={`a-${i}`}>
                        {renderLogo(name)}
                    </div>
                ))}
                {logos.map((name, i) => (
                    <div className="logo-marquee__item" key={`b-${i}`} aria-hidden="true">
                        {renderLogo(name)}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default LogoMarquee;
